import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import ScoreTypeTable from '../../../components/tables/ScoreTypeTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useScoreTypeSearch, useDeleteScoreType } from '../../../hooks/useScoreTypeApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { ScoreTypeDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const ScoreTypeList: React.FC = () => {
  const { search } = useScoreTypeSearch();
  const deleteMutation = useDeleteScoreType();
  const [items, setItems] = useState<ScoreTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Score Types', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      setIsLoading(true);
      const { results, totalCount } = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(results);
      setTotalRecords(totalCount);
    } catch (error) {
      console.error('Error fetching Score Types:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: ScoreTypeDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting ScoreType:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/scoretypes"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.ScoreType.View"
    >
      <Card
        title="Manage Score Types"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton onClick={() => navigate('/admin/scoretypes/create')} />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
            <FormField
              id="titleShort"
              label="Search by Title Short"
              value={searchFilters.titleShort || ''}
              onChange={(e) =>
                setSearchFilters({
                  ...searchFilters,
                  titleShort: e.target.value,
                })
              }
              placeholder="Search by Title Short"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <ScoreTypeTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/scoretypes/${dto.id}`)}
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default ScoreTypeList;
